import {useRef, type ReactNode} from 'react'
import type {PrunedIconButtonProps} from '../ExpandableHunkHeaderDiffLine'
import {cellIdFrom, rowIdFrom} from '../../helpers/line-helpers'
import {HunkKebabIcon} from './HunkKebabIcon'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {DiffAnchor} from '@github-ui/diffs/types'
import {useGridCellButtonProps} from '../../hooks/use-grid-cell-button-props'
import type {DiffLine} from '../../types'
import {lineAnchorFrom, lineOrientationFrom} from '@github-ui/diffs/diff-line-helpers'

/**
 * Renders a hunk header cell
 */
export function HunkCell({
  diffAnchor,
  diffLine,
  isSplit,
  renderHunkButton,
}: {
  diffAnchor: DiffAnchor
  diffLine: DiffLine
  isSplit: boolean
  renderHunkButton?: (additionalProps: PrunedIconButtonProps) => ReactNode | null
}) {
  const cellRef = useRef<HTMLTableCellElement>(null)
  const [cellProps, buttonProps] = useGridCellButtonProps(cellRef)
  const rowId = rowIdFrom(diffAnchor, diffLine, diffLine)
  const cellId = cellIdFrom(rowId, 0)
  const lineAnchor = lineAnchorFrom(diffAnchor, lineOrientationFrom(diffLine.type), diffLine.blobLineNumber)

  return (
    <td
      ref={cellRef}
      colSpan={4}
      data-grid-cell-id={cellId}
      data-line-anchor={lineAnchor}
      role="gridcell"
      style={{
        userSelect: 'none',
        position: 'relative',
        backgroundColor: 'var(--bgColor-accent-muted, var(--color-accent-subtle))',
        flexGrow: 1,
      }}
      tabIndex={-1}
      valign="top"
      /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
      className="diff-hunk-cell focusable-grid-cell left-side"
      {...cellProps}
    >
      <div className="d-flex flex-row">
        {renderHunkButton?.(buttonProps) ?? <HunkKebabIcon isSplit={isSplit} />}
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <code className="diff-text-cell hunk">
          {/* Explicitly mark html as safe because it is server-sanitized */}
          <VerifiedHTMLDiv
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            className="diff-text-inner color-fg-muted"
            // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point.
            html={diffLine.html as SafeHTMLString}
          />
        </code>
      </div>
    </td>
  )
}
